import { useState, type FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { api, buyOrder, feeOn, fmt, sellOrder, type Invoke } from '../api'

type Props = {
  account: string
  base: string
  quote: string
  side: 'buy' | 'sell'
  onSide: (s: 'buy' | 'sell') => void
  price: string
  onPrice: (p: string) => void
  balances: { symbol: string; amount: string }[]
  needsPin: boolean
  feeBps: number
  market: string
  onPlaced?: () => void
}

/**
 * Order entry. Placing an order moves money on a ledger that cannot be
 * rewound, so the form has a review step: nothing is submitted until the
 * trader has seen the exact amounts spelled out in words.
 */
export default function OrderForm({
  account, base, quote, side, onSide, price, onPrice, balances, needsPin, feeBps, market, onPlaced,
}: Props) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [amount, setAmount] = useState('')
  const [pin, setPin] = useState('')
  const [review, setReview] = useState(false)
  const [note, setNote] = useState<{ text: string; kind: 'ok' | 'err' } | null>(null)

  const held = (sym: string) => Number(balances.find(b => b.symbol === sym)?.amount ?? 0)
  const p = Number(price) || 0
  const a = Number(amount) || 0
  const total = p * a
  const spending = side === 'buy' ? quote : base
  const available = held(spending)
  // Each side is charged in the asset it receives: a buyer pays out of the base
  // it gets, a seller out of the quote. Quoting the gross would mean the ledger
  // delivering less than the form promised.
  const receiving = side === 'buy' ? base : quote
  const gross = side === 'buy' ? a : total
  const fee = feeOn(gross, feeBps)
  const net = gross - fee
  const cost = side === 'buy' ? total : a
  const over = cost > available

  const setPct = (pct: number) => {
    if (p <= 0) return
    const max = side === 'buy' ? available / p : available
    setAmount(String(Math.max(0, Math.floor(max * pct))))
  }

  const place = useMutation({
    mutationFn: () => {
      if (!account) throw new Error(t('enterAccount'))
      const values = side === 'sell' ? sellOrder(account, base, quote, a, p) : buyOrder(account, base, quote, a, p)
      return api.command<Invoke>('tuna', 'convert', account, values, pin || undefined)
    },
    onSuccess: r => {
      setNote({ text: `${t('placed')} · ${t('block')} ${r.block_number}`, kind: 'ok' })
      setAmount(''); setReview(false)
      qc.invalidateQueries({ queryKey: ['book', market] })
      qc.invalidateQueries({ queryKey: ['orders', account] })
      qc.invalidateQueries({ queryKey: ['balances', account] })
      onPlaced?.()
    },
    onError: (e: Error) => { setNote({ text: e.message, kind: 'err' }); setReview(false) },
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    setNote(null)
    if (p <= 0 || a <= 0) { setNote({ text: t('enterAmount'), kind: 'err' }); return }
    if (over) { setNote({ text: t('insufficient'), kind: 'err' }); return }
    if (needsPin && !pin) { setNote({ text: t('pinRequired'), kind: 'err' }); return }
    setReview(true)
  }

  if (review) {
    return (
      <div className="review">
        <h3>{t('confirmOrder')}</h3>
        <p className="review-line">
          {side === 'buy'
            ? t('confirmBuy', { amount: fmt(a), base, total: fmt(total, 2), quote })
            : t('confirmSell', { amount: fmt(a), base, total: fmt(total, 2), quote })}
        </p>
        <dl className="review-grid">
          <dt>{t('price')}</dt><dd className="num">{fmt(p)} {quote}</dd>
          <dt>{t('qty')}</dt><dd className="num">{fmt(a)} {base}</dd>
          <dt>{t('total')}</dt><dd className="num">{fmt(total, 2)} {quote}</dd>
          {feeBps > 0 && (
            <>
              <dt>{t('fee')} ({feeBps / 100}%)</dt>
              <dd className="num">−{fmt(fee, 2)} {receiving}</dd>
              <dt><b>{t('youReceive')}</b></dt>
              <dd className="num"><b>{fmt(net, 2)} {receiving}</b></dd>
            </>
          )}
        </dl>
        <p className="review-warn">{t('irreversible')}</p>
        <div className="review-actions">
          <button type="button" className="btn-ghost" onClick={() => setReview(false)} disabled={place.isPending}>
            {t('back')}
          </button>
          <button type="button" className={`submit ${side}`} onClick={() => place.mutate()} disabled={place.isPending}>
            {place.isPending ? t('loading') : t('confirm')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={submit}>
      <div className="side-toggle">
        <button type="button" className={side === 'buy' ? 'on-buy' : ''} onClick={() => onSide('buy')} aria-pressed={side === 'buy'}>
          {t('buy')}
        </button>
        <button type="button" className={side === 'sell' ? 'on-sell' : ''} onClick={() => onSide('sell')} aria-pressed={side === 'sell'}>
          {t('sell')}
        </button>
      </div>

      <div className="field">
        <label htmlFor="of-price">{t('price')}</label>
        <div className="input-wrap">
          <input id="of-price" inputMode="decimal" placeholder="0" value={price}
            onChange={e => onPrice(e.target.value.replace(/[^\d.]/g, ''))} />
          <span className="unit">{quote}</span>
        </div>
      </div>

      <div className="field">
        <label htmlFor="of-amount">
          {t('qty')}
          <b>{t('available')} {fmt(available, 2)} {spending}</b>
        </label>
        <div className="input-wrap">
          <input id="of-amount" inputMode="numeric" placeholder="0" value={amount}
            onChange={e => setAmount(e.target.value.replace(/[^\d]/g, ''))} />
          <span className="unit">{base}</span>
        </div>
      </div>

      <div className="pct">
        {[0.25, 0.5, 0.75, 1].map(x => (
          <button key={x} type="button" onClick={() => setPct(x)}>{x * 100}%</button>
        ))}
      </div>

      <div className="field">
        <label>{t('total')}</label>
        <div className="input-wrap">
          <input readOnly tabIndex={-1} placeholder="0" value={total ? fmt(total, 2) : ''} style={{ opacity: .85 }} />
          <span className="unit">{quote}</span>
        </div>
      </div>

      {feeBps > 0 && a > 0 && p > 0 && (
        <p className="fee-line">
          {t('feeNote', { pct: feeBps / 100 })}{' '}
          <b className="num">{fmt(net, 2)} {receiving}</b>
        </p>
      )}

      {needsPin && (
        <div className="field">
          <label htmlFor="of-pin">{t('pin')}</label>
          <div className="input-wrap">
            <input id="of-pin" type="password" inputMode="numeric" autoComplete="off" placeholder="••••"
              value={pin} onChange={e => setPin(e.target.value.replace(/[^\d]/g, ''))} />
          </div>
        </div>
      )}

      <button className={`submit ${side}`} type="submit" disabled={!account || p <= 0 || a <= 0 || over}>
        {side === 'buy' ? `${t('buy')} ${base}` : `${t('sell')} ${base}`}
      </button>

      {note && <p className={`form-note ${note.kind}`}>{note.text}</p>}
      {!note && over && <p className="form-note err">{t('insufficient')}</p>}
    </form>
  )
}
